import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const Hero = () => {
  const location = useLocation();
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const heroImages = [
    '/images/1.jpeg',
    '/images/2.jpeg',
    '/images/3.jpeg',
    '/images/4.jpeg',
    '/images/5.jpeg',
    '/images/6.jpeg',
    '/images/7.jpeg'
  ];

  // Auto-play slider
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 4000); // Change slide every 4 seconds

    return () => clearInterval(interval);
  }, [heroImages.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev - 1 + heroImages.length) % heroImages.length);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % heroImages.length);
  };

  const handleExploreClick = (e) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      // If not on homepage, navigate first then scroll
      window.location.href = '/#products';
    } else {
      // If on homepage, just scroll with offset for header
      const element = document.getElementById('products');
      if (element) {
        const headerHeight = window.innerWidth >= 768 ? 96 : 80; // md:h-24 = 96px, h-20 = 80px
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - headerHeight;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
        
        // Update URL hash
        window.history.pushState(null, '', '#products');
      }
    }
  };

  const handleLearnMoreClick = (e) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      // If not on homepage, navigate first then scroll
      window.location.href = '/#about';
    } else {
      // If on homepage, just scroll with offset for header
      const element = document.getElementById('about');
      if (element) {
        const headerHeight = window.innerWidth >= 768 ? 96 : 80; // md:h-24 = 96px, h-20 = 80px
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - headerHeight;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
        
        // Update URL hash
        window.history.pushState(null, '', '#about');
      }
    }
  };

  return (
    <section id="hero" className="relative h-screen w-full overflow-hidden">
      {/* Full Screen Slider */}
      <div className="absolute inset-0 w-full h-full">
        {heroImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100 z-0' : 'opacity-0 z-0'
            }`}
          >
            <img
              src={image}
            alt={`Luxury handcrafted rug interior ${index + 1} by Wovn Rugss – wovnrugss.com`}
              className="w-full h-full object-cover"
              style={{ objectPosition: 'center' }}
              onError={(e) => {
                e.target.src = 'https://images.unsplash.com/photo-1600166898405-da9535204843?w=1200&h=1600&fit=crop';
              }}
            />
            {/* Dark overlay for better text readability */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/20"></div>
          </div>
        ))}

        {/* Navigation Arrows */}
        <button
          onClick={goToPrevious}
          className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-30 bg-white/90 hover:bg-white rounded-full p-1.5 sm:p-2 md:p-3 shadow-lg transition-all duration-300 hover:scale-110 touch-manipulation"
          aria-label="Previous image"
        >
          <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button
          onClick={goToNext}
          className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-30 bg-white/90 hover:bg-white rounded-full p-1.5 sm:p-2 md:p-3 shadow-lg transition-all duration-300 hover:scale-110 touch-manipulation"
          aria-label="Next image"
        >
          <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* Dots Indicator */}
        <div className="absolute bottom-2 sm:bottom-4 left-1/2 -translate-x-1/2 z-30 flex gap-1.5 sm:gap-2">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`transition-all duration-300 rounded-full touch-manipulation ${
                index === currentSlide
                  ? 'w-6 sm:w-8 h-1.5 sm:h-2 bg-white'
                  : 'w-1.5 sm:w-2 h-1.5 sm:h-2 bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Content Overlay */}
      <div className="relative z-20 h-full flex items-center justify-center px-4 sm:px-6 md:px-10 lg:px-12 py-8 sm:py-12">
        <div className="w-full max-w-3xl text-center">
          <div className="flex flex-col space-y-6 sm:space-y-8 md:space-y-10 items-center">
            <div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-serif text-white leading-normal mb-3 sm:mb-4 md:mb-5 drop-shadow-lg" style={{ fontFamily: 'Georgia, serif' }}>
                Premium Handcrafted Rugs for Elegant Spaces
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-amber-200/95 font-medium mb-2 sm:mb-3 drop-shadow-md" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                Timeless Craftsmanship. Modern Living.
              </p>
              <p className="text-sm text-white/80 mb-3 drop-shadow-md">Wovn Rugss · wovnrugss.com · Woven rugs · Wovn rugs</p>
              <p className="text-sm sm:text-base md:text-lg text-white/90 leading-relaxed mb-4 sm:mb-5 md:mb-6 drop-shadow-md max-w-2xl mx-auto">
                At Wovn Rugss, we create premium woven rugs and handcrafted rugs (wovnrugs, wovenrugs) designed to elevate modern interiors. Every rug reflects timeless craftsmanship, refined aesthetics, and exceptional quality. Whether you're looking for a contemporary statement piece or a custom-designed rug tailored to your space, we bring artistry and comfort together.
              </p>
            </div>

            <div className="w-full max-w-md sm:max-w-lg md:max-w-xl flex justify-center">
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
                <button
                  onClick={handleExploreClick}
                  className="group relative bg-gradient-to-r from-amber-600 to-orange-600 text-white px-6 sm:px-8 md:px-10 py-3 sm:py-4 md:py-5 rounded-full text-sm sm:text-base md:text-lg font-bold hover:from-amber-700 hover:to-orange-700 transition-all transform hover:scale-105 shadow-xl hover:shadow-amber-500/50 overflow-hidden text-center w-full sm:w-auto"
                >
                  <span className="relative z-10 flex items-center justify-center gap-2 sm:gap-3">
                    Explore Collection
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                </button>
                <button
                  onClick={handleLearnMoreClick}
                  className="bg-white/95 backdrop-blur-sm text-amber-600 px-6 sm:px-8 md:px-10 py-3 sm:py-4 md:py-5 rounded-full text-sm sm:text-base md:text-lg font-bold hover:bg-white transition-all border-2 border-white shadow-xl hover:shadow-2xl transform hover:scale-105 text-center w-full sm:w-auto"
                >
                  Learn More
                </button>
              </div>
            </div>
            
            <div className="w-full max-w-md sm:max-w-lg md:max-w-xl flex justify-center">
              <button
                  onClick={() => {
                    window.location.href = '#contact';
                  }}
                className="inline-flex items-center justify-center gap-3 px-8 sm:px-10 md:px-12 lg:px-16 py-4 sm:py-5 md:py-6 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-full text-base sm:text-lg md:text-xl font-bold hover:from-amber-700 hover:to-orange-700 transition-all transform hover:scale-105 shadow-xl hover:shadow-amber-500/50 group w-full"
              >
                <span>Our Latest Catalogue</span>
                <svg className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;

